import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Product } from '~shared/models';
import { ProductsService } from '~app/modules/storyblok/services/products.service';
import { Direction, NUMBER_1 } from '~shared/constants';
import { GtmDatalayerService } from '~shared/services/gtm-datalayer.service';
import { ProductSelection, StoryblokButton, StoryblokImage } from '../../models';
import {
  GATrackingEventType,
  GaTrackingEventAction,
  GaTrackingEventLabel,
} from '~shared/modules/ga-tracking/constants';
import { CurrencyService } from '~shared/services/currency.service';
import { UrlUtil } from '~shared/utils/url.util';
import { Ga4TrackingHelpersService } from '~shared/modules/ga-tracking/utils';
import { TranslateService } from '@ngx-translate/core';
import { ApplicationEventType, CarouselActionType, PageNameTypes } from '~shared/modules/application-event/constants';
import { Router } from '@angular/router';
import { ScrollUtils } from '~shared/utils/scroll.utils';
import {
  ADVANCED_TYPE_OPTIONS,
  CAROUSEL_COLORS,
  CarouselColor,
  CarouselType,
  COLOR_CLASSES,
  RECOMMENDATION_TYPE_OPTIONS,
  RecommendationsType,
  TRACKING_NAMES,
  TrackingNameType,
} from '~app/modules/storyblok/constants';
import { PriceTypeService } from '~shared/modules/price-type-toggler/services';

@Component({
  selector: 'app-advanced-carousel',
  templateUrl: './advanced-carousel.component.html',
  styleUrls: ['./advanced-carousel.component.scss'],
})
export class AdvancedCarouselComponent implements OnInit, OnDestroy {
  @Input() _uid?: string;
  @Input() type: CarouselType;
  @Input() title?: string;
  @Input() color?: CarouselColor;
  @Input() subHeading?: string;
  @Input() description?: string;
  @Input() image?: StoryblokImage;
  @Input() button?: StoryblokButton[];
  @Input() productSelection?: Array<ProductSelection>;
  @Input() trackingName?: string;
  @Input() isMobile?: boolean;
  @Input() isTablet?: boolean;
  @Input() isDesktop?: boolean;
  @Input() isLandScapeTablet?: boolean;

  sbFormatParam = '/m/';
  storyBlokProducts: Product[];
  ngDestroy$ = new Subject();
  DIRECTION = Direction;
  isProductsDataLoading = true;
  private readonly CAROUSEL_TYPE: string = 'carousel';

  constructor(
    private productsService: ProductsService,
    private priceTypeService: PriceTypeService,
    private gtmDatalayerService: GtmDatalayerService,
    private currencyService: CurrencyService,
    private translateService: TranslateService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadProductsData();
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  get isPromoCarousel(): boolean {
    return this.color === CAROUSEL_COLORS.black || this.color === CAROUSEL_COLORS.whiteWithPromo;
  }

  get pageTypeName(): string {
    return UrlUtil.getPageTypeFromCurrentUrl(this.router.url);
  }

  get titleName(): string {
    return this.title || this.translateService.instant(this.getTitle());
  }

  initializeAdvancedCarouselView(): void {
    this.gtmDatalayerService.gtagEvent({
      event: GATrackingEventType.ViewItemList,
      priceType: this.priceTypeService.getPriceTypeSync(),
      ecommerce: {
        currency: this.currencyService.getCurrency(),
        item_list_name: `${this.pageTypeName} carousel|${this.titleName}|${this.trackingName || NUMBER_1}`,
        items: this.storyBlokProducts?.map((product: Product, index: number): any =>
          Ga4TrackingHelpersService.productsToEventMapper(product, this.priceTypeService.getPriceTypeSync(), index)
        ),
      },
    });
  }

  private loadProductsData(): void {
    if (this.productSelection[0].advancedProductsRecommendations) {
      this.loadRecommendedProducts();
      return;
    }
    this.loadSortedProducts();
  }

  private loadRecommendedProducts() {
    this.productsService
      .getProductsByEndpoint(this.productSelection[0].advancedProductsRecommendations.endpoint)
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe(({ items }) => {
        this.isProductsDataLoading = false;
        this.storyBlokProducts = items;
      });
  }

  private loadSortedProducts() {
    const products = this.productSelection[0].products.selectedProducts;
    this.productsService
      .getSortedProducts({
        limit: products.length,
        orderedIds: products,
      })
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe(({ items }) => {
        this.isProductsDataLoading = false;
        this.storyBlokProducts = items;
      });
  }

  getRecommendationType(): string | RecommendationsType {
    if (this.productSelection[0].products) {
      return '';
    }

    const endpoint = this.productSelection[0].advancedProductsRecommendations.endpoint;

    if (endpoint.includes('popular-products')) {
      return RECOMMENDATION_TYPE_OPTIONS.TOP_SELLER;
    }

    if (endpoint.includes('filter[promotion]=1')) {
      return RECOMMENDATION_TYPE_OPTIONS.BEST_DEALS;
    }

    return RECOMMENDATION_TYPE_OPTIONS.NEW_PRODUCTS;
  }

  getTitle(): string {
    const recommendationType: string | RecommendationsType = this.getRecommendationType();
    switch (recommendationType) {
      case RECOMMENDATION_TYPE_OPTIONS.TOP_SELLER:
        return 'ADVANCED.CAROUSEL.TOP.SELLERS.TITLE';
      case RECOMMENDATION_TYPE_OPTIONS.BEST_DEALS:
        return 'ADVANCED.CAROUSEL.BEST.DEALS.TITLE';
      case RECOMMENDATION_TYPE_OPTIONS.NEW_PRODUCTS:
        return 'ADVANCED.CAROUSEL.NEW.PRODUCTS.TITLE';
      default:
        return '';
    }
  }

  private getTrackingName(): TrackingNameType {
    if (this.pageTypeName !== PageNameTypes.Category) {
      return this._uid || '';
    }

    const recommendationType: string | RecommendationsType = this.getRecommendationType();
    switch (recommendationType) {
      case RECOMMENDATION_TYPE_OPTIONS.TOP_SELLER:
        return TRACKING_NAMES.BEST_SELLING;
      case RECOMMENDATION_TYPE_OPTIONS.BEST_DEALS:
        return TRACKING_NAMES.LATEST_DEALS;
      default:
        return TRACKING_NAMES.NEW_PRODUCTS;
    }
  }

  private scrollIntoView(): void {
    ScrollUtils.scrollToFirstOfClass('products-catalog', 'smooth');
  }

  navigateTo(): void {
    if (this.button.length) {
      const button = this.button[0];

      if (button.isExternalLink) {
        window.open(button.link, '_blank');
      } else {
        this.router.navigateByUrl(button.link);
        this.scrollIntoView();
      }
    }
  }

  onProductClickEvent({ product, priceType, productIndex }): void {
    this.gtmDatalayerService.gtagEvent({
      event: GATrackingEventType.SelectItem,
      type: this.CAROUSEL_TYPE,
      ecommerce: {
        currency: this.currencyService.getCurrency(),
        item_list_name: `${this.pageTypeName} carousel|${this.titleName}|${this.trackingName || "1"}`,
        items: [Ga4TrackingHelpersService.productToSelectItemEventMapper(product, priceType, productIndex)],
      },
    });
  }

  onButtonClickEvent(): void {
    this.gtmDatalayerService.gtagEvent({
      event: GATrackingEventType.SE_SEE_OFFERS,
      category: `${this.pageTypeName} carousel`,
      action: GaTrackingEventAction.SEE_OFFERS,
      GA_eventname: ApplicationEventType.Navigation,
      label: this.title || this.translateService.instant(this.getTitle()),
    });
  }

  onProductCarouselNavigation({ direction }): void {
    this.gtmDatalayerService.gtagEvent({
      category: ApplicationEventType.Interaction,
      action: CarouselActionType.ContentCarouselButton,
      label: direction === this.DIRECTION.RIGHT ? GaTrackingEventLabel.Right : GaTrackingEventLabel.Left,
    });
  }

  onProductCarouselSwipe({ direction }): void {
    this.gtmDatalayerService.gtagEvent({
      category: ApplicationEventType.Interaction,
      action: CarouselActionType.ContentCarouselSwipe,
      label: direction === this.DIRECTION.RIGHT ? GaTrackingEventLabel.RightSwipe : GaTrackingEventLabel.LeftSwipe,
    });
  }

  protected readonly ADVANCED_TYPE_OPTIONS = ADVANCED_TYPE_OPTIONS;
  protected readonly COLOR_CLASSES = COLOR_CLASSES;
}
